import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  FormInputText,
  InputLabel,
} from '@hopskipdrive/react-components';

import { CREATE_NEW_LOCATION_GROUP, GroupedOption, useLocationOptions } from '../../../../hooks/useLocationOptions';
import { defaultLocationState, SelectLocation } from '../../../../locations/features/MapPins/SelectLocation';
import { PageHeading } from '../../../../shared/components/PageHeading';
import { MustBeMetProtocol } from '../../MustBeMetProtocolForm';
import { Contact } from '../../MustBeMetProtocolForm/types';
import { useMustBeMetProtocol } from '../../MustBeMetProtocolForm/useMustBeMetProtocol';
import { mustBeMetProtocolValidationSchema } from '../../MustBeMetProtocolForm/validationSchema';
import { MustBeMetLabel } from '../RiderCreationModal.styles';

import { Divider, FlexRow } from './SchoolAddressForm.styles';
import { newLocationSchema } from './validationSchema';

type SchoolData = {
  school?: {
    address: GroupedOption;
    dropoffProcedureInstructions: string;
    locationType: string;
    name: string;
    pickupProcedureInstructions: string;
    streetAddressTwo: string;
  };
  mustBeMet?: boolean;
  mustBeMetProtocol?: {
    locationProtocol: string;
    authorizedContactGroups: Array<{
      name: string;
      escalationPhoneNumber: string;
      saved?: boolean;
      verifyIdBadge: boolean;
      verifyRiderName: boolean;
      verifyRiderBirthdate: boolean;
    }>;
  };
};

interface SchoolAddressProps {
  category: string;
  onSubmit: (data) => void;
}

const CreateNewLocationGroupLabel = 'Create a new location:';

export const SchoolAddressForm: FC<SchoolAddressProps> = ({ category, onSubmit }) => {
  const [shouldValidate, setShouldValidate] = useState<boolean>(false);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [placeId, setPlaceId] = useState<string | undefined>(undefined);
  const { locationOptions, debounceHandleQuery, saveNewLocation } = useLocationOptions();
  const { getMustBeMetContacts } = useMustBeMetProtocol();

  const schema = useMemo(
    () => newLocationSchema(shouldValidate).concat(mustBeMetProtocolValidationSchema()),
    [shouldValidate]
  );

  const [schoolContacts, setSchoolContacts] = useState<Contact[]>([]);

  const methods = useForm<SchoolData>({
    defaultValues: useMemo(() => {
      return {
        school: {
          address: undefined,
          streetAddressTwo: '',
          name: '',
          pickupProcedureInstructions: '',
          dropoffProcedureInstructions: '',
          locationType: 'school',
        },
        mustBeMet: false,
        mustBeMetProtocol: {
          locationProtocol: '',
          authorizedContactGroups: [],
        },
      };
    }, []),
    resolver: yupResolver(schema),
  });
  const { handleSubmit, setValue, control, resetField, clearErrors } = methods;

  const watchAddress = useWatch({
    control,
    name: 'school.address',
    defaultValue: undefined,
  });

  const watchMustBeMet = useWatch({
    control,
    name: 'mustBeMet',
    defaultValue: false,
  });

  const resetFormFieldsOnAddressChange = () => {
    resetField('mustBeMet');
    resetField('mustBeMetProtocol');
  };

  // Enable validation only when address field has a value, making the form truly optional
  useEffect(() => {
    if (!watchAddress) {
      setShouldValidate(false);
      clearErrors();
    } else {
      setShouldValidate(true);
    }
  }, [watchAddress, clearErrors]);

  useEffect(() => {
    const fetchContacts = async () => {
      const response = await getMustBeMetContacts('school', watchAddress?.id, watchAddress?.placeId, '');

      setSchoolContacts(response.contacts);
      if (response.contacts.length > 0 && response.defaultIndividualGroup) {
        setValue('mustBeMetProtocol.authorizedContactGroups', [response.defaultIndividualGroup]);
      } else {
        setValue('mustBeMetProtocol.authorizedContactGroups', []);
      }
    };

    if (watchAddress?.placeId) {
      fetchContacts();
    }
  }, [watchAddress, setValue]);

  if (category === 'older_adult') {
    return (
      <form
        id="school-address-form"
        aria-label="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit((data) => onSubmit(data))();
        }}
      >
        <Alert
          sx={{ mb: '1.5rem' }}
          severity="info"
          message="Since this is an Older Adult rider, there is no School selection in this step."
        />
      </form>
    );
  }

  return (
    <form
      id="school-address-form"
      aria-label="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit((data) => onSubmit(data))();
      }}
    >
      <FlexRow>
        <School
          control={control}
          handleSelected={(location) => {
            resetFormFieldsOnAddressChange();
            if (location) {
              if (location.group === CREATE_NEW_LOCATION_GROUP) {
                setPlaceId(location.placeId);
                setLocationId(undefined);
              } else {
                setLocationId(location.id);
                setPlaceId(undefined);
              }
            } else {
              setPlaceId(undefined);
              setLocationId(undefined);
            }
          }}
          locationOptions={locationOptions}
          debounceHandleQuery={debounceHandleQuery}
          saveNewLocation={saveNewLocation}
        />
      </FlexRow>

      <Divider />

      <Box>
        <PageHeading variant="h2" fontWeight={700} textColor="navy20" sx={{ margin: '15px 0px' }}>
          Instructions for this rider
        </PageHeading>
      </Box>

      <FlexRow>
        <Box display="flex" alignItems="center">
          <Controller
            control={control}
            name="mustBeMet"
            render={({ field: { value, onChange, onBlur } }) => {
              return (
                <Box alignItems="center" mr={6}>
                  <Checkbox
                    data-testid="mustBeMet"
                    onBlur={onBlur}
                    checked={value}
                    onChange={(e) => {
                      onChange(e.target.checked);
                    }}
                    disabled={!watchAddress}
                  />
                  <MustBeMetLabel htmlFor="mustBeMet">Rider must be met at drop-off</MustBeMetLabel>
                </Box>
              );
            }}
          />
        </Box>
      </FlexRow>
      <FormProvider {...methods}>
        <MustBeMetProtocol
          mustBeMet={watchMustBeMet}
          contacts={schoolContacts}
          accountLocationId={locationId}
          placeId={placeId}
        />
      </FormProvider>
    </form>
  );
};

const School = ({ control, handleSelected, locationOptions, debounceHandleQuery, saveNewLocation }) => {
  const filteredLocationOptions = locationOptions.filter(
    (loc) => loc.locationType === 'school' || loc.group === CreateNewLocationGroupLabel
  );

  return (
    <Box
      display="flex"
      mt="24px"
      sx={{
        width: { xs: '100%', sm: '80%' },
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
      }}
    >
      <InputLabel htmlFor="school">Address</InputLabel>
      <Controller
        control={control}
        name="school.address"
        render={({ field: { onChange, value } }) => (
          <SelectLocation
            id="school"
            data-testid="select-location"
            hidden={false}
            options={filteredLocationOptions}
            saveLocation={saveNewLocation}
            source="Ride"
            inputValue={value?.label}
            clearOnSelect={false}
            clearOnCancel={true}
            defaultsForNewLocation={{ ...defaultLocationState, locationType: 'school' }}
            setInputValue={(location) => {
              debounceHandleQuery('', { force: true });
              onChange(location);
              handleSelected(location);

              return location?.label;
            }}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <FormInputText
                {...params}
                variant="outlined"
                fullWidth
                data-testid="school-location"
                placeholder="Search"
                onChange={(e) => {
                  debounceHandleQuery(e.target.value);
                }}
              />
            )}
          />
        )}
      />
    </Box>
  );
};
